import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Loader } from 'components/_new/Loader';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { Button } from 'components/_new/Button';
import { timelineApi } from 'api/timeline';
import { ModifyTimelineEventForm } from './ModifyTimelineEventForm';

export const ProgramTimelineEventEdit = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: program, panelPath } = useProgramPanel();
	const params = useParams();
	const eventId = Number(params?.eventId);

	const hasEvent = Boolean(eventId) && !Number.isNaN(eventId);
	const {
		data: timelineEvent,
		isLoading: timelineEventLoading,
		isSuccess: timelineEventSuccess,
		isError: timelineEventIsError,
		error: timelineEventError,
		refetch: timelineEventRefetch,
		remove: timelineEventRemove,
	} = useQuery(
		['TIMELINE_EVENT', { programId: Number(program?.id), eventId }],
		() => timelineApi.program.getTimelineEventForEdit(Number(program?.id), eventId),
		{
			enabled: hasEvent,
		}
	);

	if (!hasEvent) {
		return <Navigate to={{ pathname: `/${panelPath}/timeline/list` }} />;
	}

	if (hasEvent && timelineEventIsError) {
		return <ErrorContainer action={timelineEventRefetch} error={timelineEventError} />;
	}

	if (hasEvent && timelineEventLoading) {
		return <Loader />;
	}

	if (hasEvent && timelineEventSuccess) {
		return (
			<>
				<Helmet title={t('timeline.programPanel.editTitle')} />
				<SubPageTitle
					title={t('timeline.programPanel.editTitle')}
					additional={program?.displayName}
					right={() => (
						<Button
							icon="arrow-left"
							variant="primary-text"
							variantSize="sm"
							onClick={() => {
								navigate(`/${panelPath}/timeline/list`);
							}}
							label={t('actions.back')}
						/>
					)}
				/>

				<ModifyTimelineEventForm
					timelineEvent={timelineEvent}
					onSave={() => {
						timelineEventRemove();
					}}
				/>
			</>
		);
	}

	return null;
};
